import React, { useState, useEffect } from "react";
import { App, Card, Tag, Tooltip, Button, Image, Empty, Input, InputNumber, Select, Divider } from "antd";
import { PlusOutlined, EditOutlined, StopOutlined, DollarOutlined } from "@ant-design/icons";
import { useQv4Theme, sideLabel, conditionLabel, loadSideLabels, loadConditionLabels } from "../../helpers";
import { API_DOMAIN } from "../../../constants";
import { patchQueryPart } from "../../models/shared/queryPartsModel";
import { uploadImage, downloadWatiMediaBlob } from "../../models/shared/mediaModel";
import { fetchCustomerChatImages } from "../../models/workflow-drawer/chatImagesModel";
import { fetchMiscMode } from "../../models/shared/miscModeModel";
import { useTaskParts } from "../../viewmodels/workflow-drawer/useTaskParts";
import { usePartInfoOptions } from "../../viewmodels/shared/usePartInfoOptions";
import { PartsEditor, EMPTY_PART } from "../drafts/PartsEditor";
import { QueryImagePicker } from "../drafts/QueryImagePicker";

const fmtAED = (val) => val != null ? `AED ${Number(val).toLocaleString()}` : "—";

export function PartsTab({
  parts, images, queryPk, userId, customerPhone, onPartPatched, onManagePrices,
  canManageParts = false, canEditQuery = false,
}) {
  const { message } = App.useApp();
  const { t, isDark } = useQv4Theme();
  const { disable, enable, add, appendImages } = useTaskParts(userId);
  const { options: partsInfoOptions, loading: partsInfoLoading, addOption } = usePartInfoOptions();

  const [, setLabelsReady] = useState(false);
  useEffect(() => { loadConditionLabels().then(() => setLabelsReady(v => !v)); }, []);
  useEffect(() => { loadSideLabels().then(() => setLabelsReady(v => !v)); }, []);

  const [sideOptions, setSideOptions] = useState([]);
  useEffect(() => { fetchMiscMode("part_side").then(list => setSideOptions((list || []).map(o => ({ value: o.value, label: o.label })))); }, []);
  const [conditionOptions, setConditionOptions] = useState([]);
  useEffect(() => { fetchMiscMode("part_condition_origin").then(list => setConditionOptions((list || []).map(o => ({ value: o.value, label: o.label })))); }, []);

  const [cardMode, setCardMode] = useState({});
  const [editFields, setEditFields] = useState({});
  const [disableReason, setDisableReason] = useState({});
  const [saving, setSaving] = useState({});
  const [addMode, setAddMode] = useState(false);
  const [newParts, setNewParts] = useState([{ ...EMPTY_PART }]);
  const [chatImages, setChatImages] = useState([]);
  const [chatImagesLoaded, setChatImagesLoaded] = useState(false);
  const [chatImagesLoading, setChatImagesLoading] = useState(false);
  // Standalone photo-add flow — separate from Add Part below on purpose.
  // handleAddParts requires at least one valid part before it will submit
  // anything, so a picture with no accompanying part had no way to reach the
  // query; this flow calls appendImages directly, which was already an
  // independent operation under the hood (handleAddParts just always called
  // it in the same breath as adding parts).
  const [addPhotosMode, setAddPhotosMode] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  // Shared between the two add flows above/below — either one opening
  // should be able to trigger this fetch, and it only needs to happen once
  // regardless of which one asked for it first.
  useEffect(() => {
    if (!(addMode || addPhotosMode) || chatImagesLoaded || !customerPhone) return;
    setChatImagesLoaded(true);
    setChatImagesLoading(true);
    fetchCustomerChatImages(customerPhone, userId).then(setChatImages).finally(() => setChatImagesLoading(false));
  }, [addMode, addPhotosMode, chatImagesLoaded, customerPhone, userId]);
  const [adding, setAdding] = useState(false);

  const resetAddPhotosForm = () => {
    setAddPhotosMode(false);
    setNewPhotos([]);
  };

  const handleAddPhotos = async () => {
    if (newPhotos.length === 0) { message.warning("Add at least one photo"); return; }
    setUploadingPhotos(true);
    try {
      setNewPhotos(imgs => imgs.map(img => ({ ...img, uploadStatus: "uploading" })));
      const results = await Promise.all(newPhotos.map(async img => {
        try {
          let file;
          if (img.source === "upload") {
            file = img.file;
          } else {
            const { blob } = await downloadWatiMediaBlob(img.data);
            const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
            file = new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type });
          }
          const data = await uploadImage(file);
          setNewPhotos(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "done" } : x));
          return { id: img.id, data, ok: true };
        } catch {
          setNewPhotos(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "error" } : x));
          return { id: img.id, ok: false };
        }
      }));
      if (results.some(r => !r.ok)) {
        message.error("Failed to upload one or more images. Please try again.");
        return;
      }
      await appendImages(queryPk, results.map(r => r.data));
      onPartPatched?.();
      resetAddPhotosForm();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add photo(s)");
    } finally {
      setUploadingPhotos(false);
    }
  };

  const startEdit = (p) => {
    setCardMode(m => ({ ...m, [p.id]: "editing" }));
    const matchedCondition = conditionOptions.find(o => String(o.value) === String(p.condition_preference));
    setEditFields(f => ({ ...f, [p.id]: {
      userId,
      side: p.side || "",
      part_number: p.part_number || "",
      condition_preference: matchedCondition ? matchedCondition.value : (p.condition_preference || ""),
      quantity: p.quantity || 1,
    }}));
  };

  const endMode = (partId) => setCardMode(m => ({ ...m, [partId]: null }));

  const setField = (partId, key, val) =>
    setEditFields(f => ({ ...f, [partId]: { ...f[partId], [key]: val } }));

  const handleSave = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await patchQueryPart(partId, editFields[partId]);
      onPartPatched?.();
      endMode(partId);
    } catch {
      message.error("Failed to save changes");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleDisable = async (partId) => {
    const reason = (disableReason[partId] || "").trim();
    if (!reason) { message.warning("Please enter a reason"); return; }
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await disable(partId, reason);
      onPartPatched?.();
      endMode(partId);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to disable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleEnable = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await enable(partId);
      onPartPatched?.();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to re-enable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const resetAddForm = () => {
    setAddMode(false);
    setNewParts([{ ...EMPTY_PART }]);
  };

  const handleAddNewPartInfoLocal = async (value, idx) => {
    try {
      await addOption(value);
      setNewParts(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], part_name: value };
        return next;
      });
    } catch {
      message.error("Failed to save new part name");
    }
  };

  const handleAddParts = async () => {
    const validParts = newParts.filter(p => p.part_name?.trim());
    if (validParts.length === 0) { message.warning("Add at least one part"); return; }
    setAdding(true);
    try {
      await Promise.all(validParts.map(p => add(p, queryPk)));
      onPartPatched?.();
      resetAddForm();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add part(s)");
    } finally {
      setAdding(false);
    }
  };

  const renderPartCard = (p) => {
    const isSelected = (q) => q.id === p.selected_quote_id;
    const quotes = p.quotes || [];
    const hasQuotes = quotes.length > 0;
    const isDisabled = p.status === "disabled";
    const mode = cardMode[p.id] ?? null;
    const fields = editFields[p.id] ?? {};
    const isSaving = saving[p.id] ?? false;

    return (
      <Card key={p.id} size="small"
        style={{
          borderRadius: 8,
          opacity: isDisabled ? 0.75 : 1,
          border: isDisabled ? "1px solid #ffa39e" : undefined,
          background: isDisabled ? (isDark ? "rgba(255,77,79,0.12)" : "#fff2f0") : undefined,
        }}
        styles={{ body: { padding: "10px 14px" } }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{
            fontWeight: 700, fontSize: 13,
            textDecoration: isDisabled ? "line-through" : "none",
            color: isDisabled ? "#cf1322" : undefined,
          }}>
            {p.part_name || `Part #${p.id}`}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {isDisabled
              ? <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Disabled</Tag>
              : <Tag style={{ margin: 0, fontSize: 10 }}>{p.status_label || p.status}</Tag>
            }
            {!isDisabled && canManageParts && (
              <Button size="small" type="text"
                icon={<EditOutlined style={{ fontSize: 11 }} />}
                style={{ color: mode === "editing" ? "#1677ff" : t.textSub }}
                onClick={() => mode === "editing" ? endMode(p.id) : startEdit(p)}
              />
            )}
            {!isDisabled && canManageParts && !hasQuotes && mode !== "disabling" && (
              <Tooltip title="Disable this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" danger
                  icon={<StopOutlined style={{ fontSize: 11 }} />}
                  onClick={() => setCardMode(m => ({ ...m, [p.id]: "disabling" }))}
                />
              </Tooltip>
            )}
            {!isDisabled && canManageParts && hasQuotes && mode !== "disabling" && (
              <Tooltip title="Can't disable — suppliers have already quoted on this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" disabled icon={<StopOutlined style={{ fontSize: 11 }} />} />
              </Tooltip>
            )}
            {isDisabled && canManageParts && (
              <Button size="small" loading={isSaving}
                style={{ fontSize: 10, height: 22, padding: "0 8px" }}
                onClick={() => handleEnable(p.id)}
              >
                Re-enable
              </Button>
            )}
          </div>
        </div>

        {isDisabled && p.remark && (
          <div style={{ fontSize: 11, color: "#cf1322", marginBottom: 4 }}>
            Reason: {p.remark}
          </div>
        )}

        {mode === "editing" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Side</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.side || null}
                  onChange={v => setField(p.id, "side", v ?? "")}
                  options={sideOptions}
                  placeholder="Side"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Condition</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.condition_preference || null}
                  onChange={v => setField(p.id, "condition_preference", v ?? "")}
                  options={conditionOptions}
                  placeholder="Condition"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Part Number</div>
                <Input size="small" value={fields.part_number}
                  onChange={e => setField(p.id, "part_number", e.target.value)}
                  placeholder="OEM or aftermarket #"
                />
              </div>
              <div style={{ width: 84 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Qty</div>
                <InputNumber size="small" min={1} value={fields.quantity}
                  onChange={val => setField(p.id, "quantity", val)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" type="primary" loading={isSaving} onClick={() => handleSave(p.id)}>Save</Button>
            </div>
          </div>
        )}

        {mode === "disabling" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: t.textSub, marginBottom: 4 }}>Reason for disabling</div>
            <Input.TextArea rows={2} size="small" autoFocus
              placeholder="Why is this part being removed?"
              value={disableReason[p.id] || ""}
              onChange={e => setDisableReason(r => ({ ...r, [p.id]: e.target.value }))}
              style={{ marginBottom: 6 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" danger type="primary" loading={isSaving} onClick={() => handleDisable(p.id)}>
                Confirm Disable
              </Button>
            </div>
          </div>
        )}

        {mode === null && (
          <div style={{ fontSize: 11, color: t.textSub, marginBottom: hasQuotes && !isDisabled ? 8 : 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <span>
              Qty: {p.quantity}
              {p.side ? ` · ${sideLabel(p.side)}` : ""}
              {p.condition_preference ? ` · ${conditionLabel(p.condition_preference)}` : ""}
              {p.part_number ? ` · Part# ${p.part_number}` : ""}
              {p.selling_price != null ? ` · Selling: ${fmtAED(p.selling_price)}` : ""}
            </span>
            {/* Not yet persisted on query_parts — see FUTURE_TASKS.md's
                query_parts.old_exchange spec — so this stays inert until
                that backend column ships. */}
            {p.old_exchange && <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>With Old Exchange</Tag>}
          </div>
        )}

        {!isDisabled && hasQuotes && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.textSub, marginBottom: 2 }}>
              Supplier Quotes ({quotes.length})
            </div>
            {quotes.map(q => (
              <div key={q.id} style={{
                padding: "6px 10px", borderRadius: 6, fontSize: 11,
                border: isSelected(q) ? "1.5px solid #52c41a" : `1px solid ${t.border}`,
                background: isSelected(q) ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : t.headerBg,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {q.supplier_name || `Supplier #${q.supplier_id}`}
                  </span>
                  <span style={{ color: t.textSub }}>
                    {q.quoted_price != null ? fmtAED(q.quoted_price) : "—"}
                    {q.delivery_cost != null ? ` · Delivery: ${fmtAED(q.delivery_cost)}` : ""}
                    {q.warranty_days ? ` · ${q.warranty_days}d warranty` : ""}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  {isSelected(q) && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Selected</Tag>}
                  {q.availability_status && <Tag style={{ margin: 0, fontSize: 10 }}>{q.availability_status}</Tag>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const activeParts = parts.filter(p => p.status !== "disabled");
  const disabledParts = parts.filter(p => p.status === "disabled");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {onManagePrices && (
        <Button size="small" icon={<DollarOutlined />} onClick={onManagePrices} style={{ alignSelf: "flex-start" }}>
          Manage Prices
        </Button>
      )}
      {images?.length > 0 && (
        <Image.PreviewGroup>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {images.map((src, i) => {
              const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
              return (
                <Image key={i} src={url} width={72} height={72}
                  style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "pointer", border: `1px solid ${t.border}` }}
                  preview={{ src: url }}
                />
              );
            })}
          </div>
        </Image.PreviewGroup>
      )}
      {activeParts.length === 0 && !addMode && (
        <Empty description="No parts added" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "24px 0 8px" }} />
      )}
      {activeParts.map(renderPartCard)}
      {disabledParts.length > 0 && (
        <>
          <Divider style={{ margin: "4px 0", fontSize: 11, color: t.textSub }}>
            Disabled ({disabledParts.length})
          </Divider>
          {disabledParts.map(renderPartCard)}
        </>
      )}

      {addMode && (
        <Card size="small" style={{ borderRadius: 8, border: "1.5px dashed #1677ff" }}
          styles={{ body: { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 } }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#1677ff" }}>Add to this query</div>
          <PartsEditor
            parts={newParts}
            setParts={updater => setNewParts(prev => typeof updater === "function" ? updater(prev) : updater)}
            sideOptions={sideOptions}
            conditionOptions={conditionOptions}
            partsInfoOptions={partsInfoOptions}
            partsInfoLoading={partsInfoLoading}
            onAddNewPartInfo={handleAddNewPartInfoLocal}
            label="New parts"
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <Button size="small" disabled={adding} onClick={resetAddForm}>Cancel</Button>
            <Button size="small" type="primary" loading={adding} onClick={handleAddParts}>Save</Button>
          </div>
        </Card>
      )}

      {addPhotosMode && (
        <Card size="small" style={{ borderRadius: 8, border: "1.5px dashed #1677ff" }}
          styles={{ body: { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 } }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#1677ff" }}>Add photos to this query</div>
          <QueryImagePicker
            images={newPhotos}
            setImages={updater => setNewPhotos(prev => typeof updater === "function" ? updater(prev) : updater)}
            imageMessages={chatImages}
            imageMessagesLoading={chatImagesLoading}
            label="New photos"
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <Button size="small" disabled={uploadingPhotos} onClick={resetAddPhotosForm}>Cancel</Button>
            <Button size="small" type="primary" loading={uploadingPhotos} onClick={handleAddPhotos}>Save</Button>
          </div>
        </Card>
      )}

      {!addMode && !addPhotosMode && (canManageParts || canEditQuery) && (
        <div style={{ display: "flex", gap: 8 }}>
          {canManageParts && <Button type="dashed" size="small" block icon={<PlusOutlined />}
            style={{ color: "#1677ff", borderColor: "#1677ff" }}
            onClick={() => setAddMode(true)}
          >
            Add Part
          </Button>}
          {canEditQuery && <Button type="dashed" size="small" block icon={<PlusOutlined />}
            style={{ color: "#1677ff", borderColor: "#1677ff" }}
            onClick={() => setAddPhotosMode(true)}
          >
            Add Photos
          </Button>}
        </div>
      )}
    </div>
  );
}
